/* eslint-disable react/no-unused-prop-types,no-console,
react/no-danger,react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import ContentLoader from 'react-content-loader';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
// material-ui icons
import Done from '@material-ui/icons/Done';
import Edit from '@material-ui/icons/Edit';
import Check from '@material-ui/icons/Check';
import ArrowForward from '@material-ui/icons/ArrowForward';
import LinearProgress from '@material-ui/core/LinearProgress';
import Refresh from '@material-ui/icons/Refresh';
import AddIco from '@material-ui/icons/AddCircle';
import Publish from '@material-ui/icons/CloudUpload';
import UnPublish from '@material-ui/icons/CloudDownload';
// material-ui/ core components
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import Checkbox from '@material-ui/core/Checkbox';
import { Zoom, Tooltip } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import Snackbar from 'components/UI/Snackbar/Snackbar';
import Badge from 'components/UI/Badge/Badge';
// global variables
import { adStatus, flags } from 'config';
// our components
import RouterContext from 'helpers/routerContext';
import { LoadingAnimation } from 'components/common/Loading';
import Paginator from 'components/common/Paginator';
import { TableLoader } from 'components/common/TableLoader';
import Filter from 'components/FlaggedAd/Filter';
import Order from 'components/FlaggedAd/Order';
import WithPermission from 'components/User/WithPermission';
import { FLAGGED_ADS, USER } from 'routes/paths';
import dateFormat from 'helpers/dateHelper';
import ImageMosaic from 'components/common/ImageMosaic/ImageMosaic';

/**
 * @description - FlaggedAdList component
 * @augments React.Component
 */
class FlaggedAdList extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      selectedItems: [],
      showCollapseFilters: true,
    };
  }

  /**
   * @description
   * Select all items.
   *
   * @param {Array} edges - All ads from api.
   * @returns {undefined} No return.
   */
  handleSelectAll = edges => {
    const { selectedItems } = this.state;
    // if all are selected
    if (selectedItems.length === edges.length) {
      this.setState({ selectedItems: [] });
    } else {
      this.setState({ selectedItems: edges.map(item => item.node.id) });
    }
  };

  /**
   * @description
   * Select an item.
   *
   * @param {number} id - Id of selected item.
   * @returns {undefined} No return.
   */
  handleSelectItem = id => {
    const { selectedItems } = this.state;
    // if item is selected
    if (selectedItems.indexOf(id) !== -1) {
      this.setState({
        selectedItems: selectedItems.filter(item => item !== id),
      });
    } else {
      this.setState({ selectedItems: [...selectedItems, id] });
    }
  };

  /**
   * @description
   * Handle ad actions.
   *
   * @param {string} action - Specific action.
   * @param {Array} selectedItems - Items ids.
   */
  handleAction = (action, selectedItems) => {
    const { adPublish, adUnPublish, adReview, adUnReview } = this.props;
    if (action === 'review') {
      adReview(selectedItems);
    } else if (action === 'unreview') {
      adUnReview(selectedItems);
    } else if (action === 'publish') {
      adPublish(selectedItems);
    } else if (action === 'unpublish') {
      adUnPublish(selectedItems);
    }
    this.setState({ selectedItems: [] });
  };

  /**
   * @description
   * Redirect to user/read route.
   *
   * @param {number} id - Id of the user to read.
   */
  handleReadUser = id => {
    const history = this.context;
    history.push(formatRoute(USER.DETAILS, { id }));
  };

  /**
   * @description
   * Handle show collapsed or full filters.
   */
  handleShowFilters = () => {
    const { showCollapseFilters } = this.state;
    this.setState({ showCollapseFilters: !showCollapseFilters });
  };

  /**
   * @description
   * Call refresh callback.
   */
  handleRefresh = () => {
    const { refresh } = this.props;
    refresh();
  };

  /**
   * @description
   * Description of ReactTable columns.
   *
   * @param {Array} edges - All ads from api.
   * @returns {Array} Table columns.
   */
  renderColumns = edges => {
    const { selectedItems } = this.state;
    const { classes, t, appliedOrder } = this.props;
    return [
      {
        Header: (
          <Checkbox
            onClick={() => this.handleSelectAll(edges)}
            checked={edges && selectedItems.length === edges.length}
            id="allCheckbox"
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{
              checked: classes.checked,
            }}
          />
        ),
        accessor: 'checkbox',
        sortable: false,
        filterable: false,
        maxWidth: 58,
      },
      {
        Header: (
          <div
            className={
              appliedOrder.orderColumn === 'flags'
                ? classes.sortField
                : classes.headerField
            }
            style={{ textAlign: 'left' }}
          >
            {t('flagged_ad.list.headers.title')}
          </div>
        ),
        accessor: 'title',
        sortable: false,
        filterable: false,
        minWidth: 250,
      },
      {
        Header: (
          <div
            className={
              appliedOrder.orderColumn === 'lastFlag'
                ? classes.sortField
                : classes.headerField
            }
            style={{ textAlign: 'left' }}
          >
            {t('flagged_ad.list.headers.flags')}
          </div>
        ),
        accessor: 'flags',
        sortable: false,
        filterable: false,
        minWidth: 180,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('flagged_ad.list.headers.ipDate')}
          </span>
        ),
        accessor: 'ipDate',
        sortable: false,
        filterable: false,
        minWidth: 85,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('flagged_ad.list.headers.user')}
          </span>
        ),
        accessor: 'user',
        sortable: false,
        filterable: false,
        maxWidth: 250,
      },
      {
        Header: (
          <span className={classes.headerField}>
            <p>{t('flagged_ad.list.headers.review')}</p>
          </span>
        ),
        accessor: 'review',
        sortable: false,
        filterable: false,
        maxWidth: 100,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            <p>{t('flagged_ad.list.headers.actions')}</p>
          </span>
        ),
        accessor: 'actions',
        sortable: false,
        filterable: false,
        maxWidth: 80,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  /**
   * @description
   * Render Flags cell.
   *
   * @param {Object} node - An ad.
   * @returns {*} Flags info cell.
   */
  renderCellFlagsInfo = node => {
    const { classes, t } = this.props;

    return Object.keys(flags).map(flag =>
      node[flag].total > 0 ? (
        <div
          className={`${classes.verticalAlignment}`}
          style={{ justifyContent: 'space-between' }}
          key={`flag-${flags[flag].name}`}
        >
          <div>
            <Badge color="gray">{node[flag].total}</Badge>
            <span style={{ marginLeft: '8px' }}>
              <b>{flags[flag].name}</b>
            </span>{' '}
            •
            <span style={{ marginLeft: '5px' }}>
              {dateFormat(node[flag].lastFlaggedOn, t)}
            </span>
          </div>
        </div>
      ) : null,
    );
  };

  /**
   * @description
   * To give a row color depending of column status.
   *
   * @param {number} nodeId - Id of an Ad.
   * @returns {string} Row color.
   */
  renderRowColor = nodeId => {
    const { data } = this.props;
    const node = data.flaggedAds.edges.find(
      ({ node: { id } }) => id === nodeId,
    );
    switch (node.node.status) {
      case adStatus.PENDING_VERIFICATION || adStatus.PROCESSING:
        return 'rgba(255, 181, 0, .1)';
      case adStatus.UNPUBLISHED_BY_USER:
        return 'rgba(81, 0, 14, .1)';
      case adStatus.UNPUBLISHED_BY_USER_DELETED:
        return 'rgba(81, 0, 14, .1)';
      case adStatus.UNPUBLISHED_BY_USER_BLOCKED:
        return 'rgba(81, 0, 14, .1)';
      default:
        if (node.node.reviewed) {
          return 'rgba(76, 175, 80, 0.15)';
        }
        return '';
    }
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Array} edges - All ads from api.
   * @returns {Array} Table rows.
   */
  renderRows = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    const history = this.context;
    return edges.map(({ node }) => ({
      id: node.id,
      checkbox: (
        <Checkbox
          onClick={() => {
            this.handleSelectItem(node.id);
          }}
          checked={selectedItems.indexOf(node.id) !== -1}
          checkedIcon={<Check className={classes.checkedIcon} />}
          icon={<Check className={classes.uncheckedIcon} />}
          classes={{
            checked: classes.checked,
          }}
        />
      ),
      title: (
        <WithPermission
          alt={
            <div
              id={`ad-title-${node.id}`}
              data-cy="adTitle"
              className={classes.adTitleContainer}
            >
              {node.readyImages && (
                <ImageMosaic
                  images={node.readyImages.map(({ id, urls }) => ({
                    id,
                    ...urls,
                  }))}
                />
              )}
              <div className={classes.idAndDate}>
                <WithPermission
                  allowPermissions={[
                    'view_autopaidad',
                    'view_ownership_autopaidad',
                  ]}
                >
                  {node.isAuto && <span className={classes.adAuto}>AUTO</span>}
                </WithPermission>
                <p>
                  <b data-cy={node.id}>ID: {node.id}</b>
                  {node.flagCount > 0 && (
                    <span style={{ marginLeft: 8 }}>
                      <Badge color="danger">{node.flagCount}</Badge>
                    </span>
                  )}
                </p>
              </div>
              <p className={classes.adTitle}>{node.title}</p>
            </div>
          }
          allowPermissions={['view_ad']}
        >
          <div
            style={{ padding: '8px 0' }}
            id={`ad-title-${node.id}`}
            data-cy="adTitle"
          >
            {node.readyImages && (
              <ImageMosaic
                images={node.readyImages.map(({ id, urls }) => ({
                  id,
                  ...urls,
                }))}
              />
            )}
            <div className={classes.idAndDate}>
              <WithPermission
                allowPermissions={[
                  'view_autopaidad',
                  'view_ownership_autopaidad',
                ]}
              >
                {node.isAuto && <span className={classes.adAuto}>AUTO</span>}
              </WithPermission>
              <p style={{ marginRight: 8 }}>
                <b data-cy={node.id}>ID: {node.id}</b>
                {node.flagCount > 0 && (
                  <span style={{ marginLeft: 8 }}>
                    <Badge color="danger">{node.flagCount}</Badge>
                  </span>
                )}
              </p>
            </div>
            <ul
              key={`ad-subcategory-${node.id}`}
              className={classes.adCatBread}
            >
              <li data-cy="adCategory">
                {node.subcategory.parentCategory.title}
              </li>
              <li className={classes.adIconBread}>
                <ArrowForward color="disabled" />
              </li>
              <li data-cy="adSubcategory">{node.subcategory.title}</li>
            </ul>
            <a
              className={['read', classes.adBtns, classes.titleBtn].join(' ')}
              href={formatRoute(FLAGGED_ADS.DETAILS, { id: node.id })}
              onClick={e => {
                e.preventDefault();
                history.push(formatRoute(FLAGGED_ADS.DETAILS, { id: node.id }));
              }}
            >
              {node.title}
            </a>
          </div>
        </WithPermission>
      ),
      flags: <div data-cy="status">{this.renderCellFlagsInfo(node)}</div>,
      ipDate: (
        <React.Fragment>
          <div className={[classes.botScore].join(' ')}>
            <AddIco />
            {node.createdOn && (
              <span style={{ marginLeft: 4 }}>
                {dateFormat(node.createdOn, t)}
              </span>
            )}
          </div>
          {node.ipInsert !== null && (
            <p style={{ marginLeft: '18px' }}>{node.ipInsert}</p>
          )}
          {node.updateCount > 0 && (
            <React.Fragment>
              <div className={[classes.botScore, classes.idAndDate].join(' ')}>
                <Edit />
                {node.updatedOn && (
                  <span style={{ marginLeft: 4 }}>
                    {dateFormat(node.updatedOn, t)}
                  </span>
                )}
              </div>
              {node.ipUpdate !== null && (
                <p style={{ marginLeft: '18px' }}>{node.ipUpdate}</p>
              )}
            </React.Fragment>
          )}
        </React.Fragment>
      ),
      user: (
        <div data-cy="adUserInfo" className={classes.adUserContainer}>
          {node.user ? (
            <WithPermission
              alt={<span>{node.user.fullname}</span>}
              allowPermissions={['view_user']}
            >
              <Button
                simple
                color="primary"
                className={['read', classes.adBtns, classes.titleBtn].join(' ')}
                onClick={() => this.handleReadUser(node.user.id)}
              >
                {node.user.fullname}
              </Button>
            </WithPermission>
          ) : (
            <p>
              <b>{node.name}</b>
            </p>
          )}
          <p data-cy="adUserEmail" key={`user-email-${node.id}`}>
            {node.user ? node.user.email : node.email}
          </p>
          <p data-cy="adUserPhone" key={`user-phone-${node.id}`}>
            {node.user ? node.user.phone : node.phone}
          </p>
        </div>
      ),
      review: (
        <React.Fragment>
          {node.reviewed ? (
            <div className={classes.reviewCheck}>
              <span>
                <Done />
                <WithPermission
                  alt={
                    <span>{node.reviewedBy && node.reviewedBy.fullname}</span>
                  }
                  allowPermissions={['view_user']}
                >
                  <Button
                    data-cy="adReviewedByBtn"
                    id={`ads-reviewed-${node.id}`}
                    className={classes.adBtns}
                    simple
                    size="sm"
                    color="primary"
                    onClick={() => this.handleReadUser(node.reviewedBy.id)}
                  >
                    {node.reviewedBy && node.reviewedBy.fullname}
                  </Button>
                </WithPermission>
              </span>
              <span className={classes.dateFormat}>
                {dateFormat(node.reviewedOn, t)}
              </span>
            </div>
          ) : (
            <WithPermission allowPermissions={['review_ad']}>
              <Button
                data-cy="reviewAd"
                color="primary"
                onClick={() => this.handleAction('review', [node.id])}
                id={`review-ad-${node.id}`}
              >
                {t('common.review')}
              </Button>
            </WithPermission>
          )}
        </React.Fragment>
      ),
      actions: selectedItems.indexOf(node.id) === -1 && (
        // we've added some custom button actions
        <div className="actions-center">
          {/* use this button to unpublish the data row */}
          {(node.status === adStatus.PUBLISHED_BY_MODERATOR ||
            node.status === adStatus.PUBLISHED_BY_PROCESSOR ||
            node.status === adStatus.TO_REVIEW) && (
            <WithPermission allowPermissions={['publish_ad']}>
              <Tooltip
                placement="top"
                TransitionComponent={Zoom}
                title={
                  <React.Fragment>
                    {t('common.unpublish')}
                    <span className={classes.arrow} />
                  </React.Fragment>
                }
                classes={{
                  tooltip: classes.bootstrapTooltip,
                  tooltipPlacementTop: classes.bootstrapPlacementTop,
                }}
              >
                <Button
                  data-cy="adPublishOrUnpublishBtn"
                  justIcon
                  onClick={() => this.handleAction('unpublish', [node.id])}
                  className={`ad-published-${node.id}`}
                  id={`unpublish-ad-${node.id}`}
                  color="danger"
                >
                  <UnPublish />
                </Button>
              </Tooltip>
            </WithPermission>
          )}
          {/* use this button to publish the data row */}
          {(node.status === adStatus.UNPUBLISHED_BY_MODERATOR ||
            node.status === adStatus.UNPUBLISHED_BY_PROCESSOR ||
            node.status === adStatus.TO_REVIEW) && (
            <WithPermission allowPermissions={['publish_ad']}>
              <Tooltip
                placement="top"
                TransitionComponent={Zoom}
                title={
                  <React.Fragment>
                    {t('common.publish')}
                    <span className={classes.arrow} />
                  </React.Fragment>
                }
                classes={{
                  tooltip: classes.bootstrapTooltip,
                  tooltipPlacementTop: classes.bootstrapPlacementTop,
                }}
              >
                <Button
                  data-cy="adPublishOrUnpublishBtn"
                  justIcon
                  onClick={() => this.handleAction('publish', [node.id])}
                  className={`ad-unpublished-${node.id}`}
                  color="success"
                  id={`publish-ad-${node.id}`}
                >
                  <Publish />
                </Button>
              </Tooltip>
            </WithPermission>
          )}
        </div>
      ),
    }));
  };

  /**
   * @description
   * Map Loading index Rows with itemProcessing.
   *
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @param {Object} edges - Edges of connection data.
   */
  mapLoadingRows = (itemProcessing, edges) => {
    const loadingRows = {};
    edges.forEach((value, index) => {
      if (itemProcessing.includes(value.node.id)) {
        loadingRows[index] = true;
      }
    });
    return loadingRows;
  };

  /**
   * @description
   * Render Filter, Paginator and ReactTable components.
   * @returns {*} Filter, Paginator and ReactTable components.
   */
  render() {
    const {
      data,
      loading,
      fullLoading,
      changeNumberOfRows,
      changePage,
      numberOfRowsSelected,
      paginationDirection,
      classes,
      filter,
      restartFilters,
      appliedFilter,
      itemProcessing,
      t,
      failedIds,
      showNotification,
      closeNotification,
      appliedOrder,
      changeOrder,
    } = this.props;
    const { selectedItems, showCollapseFilters } = this.state;
    const edges = loading || data === undefined ? [] : data.flaggedAds.edges;
    const rows = this.renderRows(edges);
    const cols = this.renderColumns(edges);
    const loadingRows = this.mapLoadingRows(itemProcessing, edges);
    return (
      <React.Fragment>
        <Snackbar
          place="tc"
          color="danger"
          message={t(`common.failed_bulk_action`, {
            count: failedIds.length,
          })}
          open={showNotification}
          closeNotification={closeNotification}
          close
        />
        <div className={classes.pageContainer}>
          <GridContainer justify="space-between" alignItems="center">
            <GridItem>
              <h3 className={classes.title}>{t('flagged_ad.list.title')}</h3>
            </GridItem>
            <GridItem>
              <Button
                color="primary"
                simple
                onClick={this.handleRefresh}
                id="refresh"
                key="refresh"
              >
                <Refresh />
                {t('ad.filters.update')}
              </Button>
            </GridItem>
          </GridContainer>

          <Filter
            filter={filter}
            appliedFilter={appliedFilter}
            restartFilters={restartFilters}
            showCollapseFilters={showCollapseFilters}
            showFilters={this.handleShowFilters}
          />

          <div className={classes.contentContainer}>
            <div
              className={[classes.loadingLineContainer, classes.topLine].join(
                ' ',
              )}
            >
              {' '}
              {fullLoading && (
                <LinearProgress
                  classes={{
                    barColorPrimary: classes.loadingLine,
                  }}
                />
              )}
            </div>
            <GridContainer>
              <GridItem
                className={[
                  classes.groupActions,
                  classes.loteActionsAnimation,
                  selectedItems.length > 0
                    ? classes.showLote
                    : classes.hideLote,
                ].join(' ')}
                xs={12}
              >
                <WithPermission allowPermissions={['publish_ad']}>
                  <Button
                    data-cy="publishLote"
                    color="success"
                    simple
                    onClick={() => this.handleAction('publish', selectedItems)}
                    id="publishAd"
                  >
                    <Publish />
                    {t('common.publish')}
                  </Button>
                  <Button
                    data-cy="unpublishLote"
                    color="danger"
                    simple
                    onClick={() =>
                      this.handleAction('unpublish', selectedItems)
                    }
                    id="unpublishAd"
                  >
                    <UnPublish />
                    {t('common.unpublish')}
                  </Button>
                </WithPermission>
                <WithPermission allowPermissions={['review_ad']}>
                  <Button
                    data-cy="reviewLote"
                    color="primary"
                    simple
                    onClick={() => this.handleAction('review', selectedItems)}
                    id="reviewAd"
                  >
                    <Done />
                    {t('common.review')}
                  </Button>
                  <Button
                    data-cy="unreviewLote"
                    color="primary"
                    simple
                    onClick={() => this.handleAction('unreview', selectedItems)}
                    id="unreviewAd"
                  >
                    <Done />
                    {t('common.unreview')}
                  </Button>
                </WithPermission>
              </GridItem>
              <GridItem
                className={[
                  classes.z2,
                  classes.loteActionsAnimation,
                  selectedItems.length > 0 ? classes.hideLote : '',
                ].join(' ')}
                xs={12}
              >
                <GridContainer justify="flex-end" alignItems="center">
                  <GridItem xs={12} sm={12} md={6} lg={8}>
                    <Order
                      appliedOrder={appliedOrder}
                      changeOrder={changeOrder}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={8} md={6} lg={4}>
                    {!fullLoading ? (
                      <Paginator
                        pageInfo={data.flaggedAds.pageInfo}
                        changePage={changePage}
                        changeNumberOfRows={changeNumberOfRows}
                        paginationDirection={paginationDirection}
                        numberOfRowsSelected={numberOfRowsSelected}
                      />
                    ) : (
                      <ContentLoader height={67} width={327} speed={2}>
                        <path
                          // eslint-disable-next-line max-len
                          d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                          fill="#D8D8D8"
                        />
                      </ContentLoader>
                    )}
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem xs={12} className={classes.z3}>
                <GridContainer>
                  <GridItem xs={12}>
                    {loading ? (
                      <TableLoader />
                    ) : (
                      <ReactTable
                        data={rows}
                        filterable
                        columns={cols}
                        loading={loading}
                        pageSize={
                          edges.length < numberOfRowsSelected
                            ? edges.length
                            : numberOfRowsSelected
                        }
                        showPagination={false}
                        className={['-striped -highlight', classes.tbl].join(
                          ' ',
                        )}
                        expanded={loadingRows}
                        defaultFilterMethod={(field, row) => {
                          const id = field.pivotId || field.id;
                          return row[id] !== undefined
                            ? String(row[id]).startsWith(field.value)
                            : true;
                        }}
                        SubComponent={() => (
                          <div className={classes.loading}>
                            <LoadingAnimation />
                          </div>
                        )}
                        getTrProps={(state, rowInfo) => ({
                          style: {
                            backgroundColor:
                              rowInfo &&
                              (selectedItems.indexOf(rowInfo.original.id) !== -1
                                ? 'rgba(171, 221, 250, .8)'
                                : this.renderRowColor(rowInfo.original.id)),
                            boxShadow:
                              rowInfo &&
                              failedIds.indexOf(rowInfo.original.id) !== -1
                                ? 'inset 4px 0 0 red'
                                : '',
                          },
                        })}
                        noDataText={t('common.no_data')}
                        minRows={8}
                      />
                    )}
                  </GridItem>
                </GridContainer>
                <GridContainer justify="flex-end" alignItems="center">
                  <GridItem xs={12} sm={8} md={6} lg={4}>
                    {!fullLoading ? (
                      <Paginator
                        pageInfo={data.flaggedAds.pageInfo}
                        changePage={changePage}
                        changeNumberOfRows={changeNumberOfRows}
                        paginationDirection={paginationDirection}
                        numberOfRowsSelected={numberOfRowsSelected}
                      />
                    ) : (
                      <ContentLoader height={67} width={327} speed={2}>
                        <path
                          // eslint-disable-next-line max-len
                          d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                          id="Combined-Shape"
                          fill="#D8D8D8"
                        />
                      </ContentLoader>
                    )}
                  </GridItem>
                </GridContainer>
              </GridItem>
            </GridContainer>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

/**
 * FlaggedAdList PropTypes
 * @param classes - prop styles received from withStyles
 * @param data - prop ads query's results
 * @param itemProcessing - id of items that is processing
 * @param numberOfRowsSelected - prop current number of rows selected
 * @param appliedFilter - prop pass to Filter
 * @param appliedOrder - prop pass to Order
 * @param loading - prop ads query or mutations status
 * @param t - prop from HOC with string translations
 * @param failedIds - id of items that was failed after bulk action
 * @param showNotification - prop to know if notification will be showed or not
 * @callback adPublish - callback to publish elements
 * @callback adUnPublish - callback to unpublish elements
 * @callback adReview - callback to review elements
 * @callback adUnReview - callback to unReview elements
 * @callback changePage - prop pass to Paginator
 * @callback changeNumberOfRows - prop pass to Paginator
 * @callback filter - prop pass to Filter
 * @callback restartFilters - prop pass to Filter
 * @callback changeOrder - prop pass to Order
 * @callback refresh - callback to refresh page
 * @callback closeNotification - callback to close notification
 */

FlaggedAdList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  itemProcessing: PropTypes.instanceOf(Array).isRequired,
  data: PropTypes.shape({}).isRequired,
  numberOfRowsSelected: PropTypes.number.isRequired,
  // eslint-disable-next-line react/require-default-props
  paginationDirection: PropTypes.string,
  adPublish: PropTypes.func.isRequired,
  adUnPublish: PropTypes.func.isRequired,
  adReview: PropTypes.func.isRequired,
  adUnReview: PropTypes.func.isRequired,
  changePage: PropTypes.func.isRequired,
  changeNumberOfRows: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  appliedOrder: PropTypes.shape({}).isRequired,
  changeOrder: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  failedIds: PropTypes.instanceOf(Array).isRequired,
  showNotification: PropTypes.bool.isRequired,
  closeNotification: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const FlaggedAdWrapper = props => <FlaggedAdList {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(FlaggedAdWrapper),
);
